import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { dbui_v1_WarehouseItem } from '@antimatterhq/asl-api-ts-v1';
import toast from 'react-hot-toast';
import { datadogRum } from '@datadog/browser-rum';
import { useGetWarehouseLocations } from '@/hooks/dbui/useGetWarehouseLocations';
import {
  useCreateStartWarehouseById,
  WarehouseStartState,
} from '@/hooks/dbui/useCreateStartWarehouseById';

export const MAX_RETRY_ATTEMPTS = 3;
export const ENABLE_WAREHOUSE_MANAGER_DEBUG_LOGGING = false;

// Helper function to create a warehouse start state
function createWarehouseStartState(
  args?: Partial<WarehouseStartState>,
): WarehouseStartState {
  return {
    attempts: args?.attempts ?? 0,
    lastAttemptTime: args?.lastAttemptTime ?? 0,
    lastSuccessTime: args?.lastSuccessTime ?? 0,
    isStarting: args?.isStarting ?? false,
    error: args?.error,
  };
}

function useWarehouseLocationManager() {
  const { data, isRefetching } = useGetWarehouseLocations();
  const { mutateAsync: startWarehouseById } = useCreateStartWarehouseById();
  const warehouseStateRef = useRef<{ [key: string]: WarehouseStartState }>({});

  const [selectedWarehouseId, setSelectedWarehouseId] = useState<
    string | undefined
  >(undefined);

  // This should call startWarehouseIfNeeded after every refetch. The startWarehouseIfNeeded
  // logic should handle the retrying logic as well as the logic to not start a warehouse that is not stopped.
  const startWarehouseIfNeeded = useCallback(
    async (warehouse: dbui_v1_WarehouseItem) => {
      const warehouseId = warehouse.id;
      const state =
        warehouseStateRef.current[warehouseId] ?? createWarehouseStartState();

      // If the warehouse is not stopped, skip the start attempt
      if (warehouse.state !== 'STOPPED') {
        if (ENABLE_WAREHOUSE_MANAGER_DEBUG_LOGGING) {
          console.warn(
            `Warehouse ${warehouseId} is not stopped, skipping start attempt`,
          );
        }

        return;
      }

      // If the warehouse start is already in progress, skip the start attempt
      if (state.isStarting) {
        if (ENABLE_WAREHOUSE_MANAGER_DEBUG_LOGGING) {
          console.warn(
            `Warehouse ${warehouseId} start already in progress, skipping`,
          );
        }
        return;
      }

      const now = Date.now();

      // If the warehouse has already been started successfully, and the auto stop time has not passed, skip the start attempt
      if (
        state.lastSuccessTime > 0 &&
        !(now - state.lastSuccessTime < 1000 * 60 + warehouse.auto_stop_mins)
      ) {
        if (ENABLE_WAREHOUSE_MANAGER_DEBUG_LOGGING) {
          console.warn(
            `Warehouse ${warehouseId} has already been started successfully, skipping`,
          );
        }
        return;
      }

      // Exponential backoff with a max of 30 seconds
      const backoffDelay = Math.min(Math.pow(2, state.attempts) * 2000, 30000);
      const canAttemptToStartWarehouse =
        state.attempts < MAX_RETRY_ATTEMPTS &&
        now - state.lastAttemptTime >= backoffDelay;

      // If the warehouse has already been started the max number of times, skip the start attempt and toast an error
      if (!canAttemptToStartWarehouse && state.attempts >= MAX_RETRY_ATTEMPTS) {
        if (ENABLE_WAREHOUSE_MANAGER_DEBUG_LOGGING) {
          console.warn(
            `Warehouse ${warehouseId} could not be started after ${MAX_RETRY_ATTEMPTS} attempts`,
          );
        }
        toast.error(
          state.error?.message ??
            `Warehouse "${warehouse.name}" could not be started`,
        );
        // TODO: Should we delete the warehouse start state here? Currently if it fails this won't be retried until the user reloads the page
        return;
      }

      // Update the warehouse start state to indicate that the warehouse is starting
      warehouseStateRef.current[warehouseId] = createWarehouseStartState({
        ...state,
        isStarting: true,
        lastAttemptTime: now,
        lastSuccessTime: 0,
      });

      // Start the warehouse
      await startWarehouseById(
        { id: warehouseId, state: warehouseStateRef.current[warehouseId] },
        {
          onSuccess: async () => {
            // Delete the warehouse start state if the warehouse was started successfully
            warehouseStateRef.current[warehouseId] = createWarehouseStartState({
              isStarting: false,
              lastSuccessTime: Date.now(),
            });
          },
          onError: async ({ error, variables }) => {
            // Update the warehouse start state to indicate that the warehouse failed to start
            const { id, state: warehouseStartState } = variables;
            warehouseStateRef.current[id] = createWarehouseStartState({
              isStarting: false,
              error,
              attempts: (warehouseStartState?.attempts ?? 0) + 1,
              lastAttemptTime:
                warehouseStartState?.lastAttemptTime ?? Date.now(),
            });
          },
        },
      );
    },
    [startWarehouseById],
  );

  const setSelectedWarehouse = useCallback(
    (warehouse: dbui_v1_WarehouseItem | undefined) => {
      setSelectedWarehouseId(warehouse?.id);
    },
    [],
  );

  const selectedWarehouse = useMemo(() => {
    return data?.warehouses.find((w) => w.id === selectedWarehouseId);
  }, [data?.warehouses, selectedWarehouseId]);

  useEffect(() => {
    // If there are warehouses and no selected warehouse, select the warehouse that matches "DASL Warehouse"
    if (!!data?.warehouses?.length && !selectedWarehouse) {
      const daslWarehouse = data.warehouses.find(
        (w) => w.name.toLocaleLowerCase() === 'dasl warehouse',
      );

      if (daslWarehouse) {
        setSelectedWarehouse(daslWarehouse);
      } else {
        const error = new Error(
          `No warehouse found with name "DASL Warehouse" - available: ${data.warehouses.map((w) => w.name).join(', ')}`,
        );
        datadogRum.addError(error);
      }
    }
  }, [data?.warehouses, setSelectedWarehouse, selectedWarehouse]);

  useEffect(() => {
    // Only start the warehouse if it is stopped and not currently being refetched
    if (
      selectedWarehouse &&
      !isRefetching &&
      selectedWarehouse.state === 'STOPPED'
    ) {
      startWarehouseIfNeeded(selectedWarehouse);
    }
  }, [selectedWarehouse, startWarehouseIfNeeded, data, isRefetching]);

  // Cleanup if the component unmounts
  useEffect(() => {
    return () => {
      warehouseStateRef.current = {};
    };
  }, []);

  return {
    selectedWarehouse,
    setSelectedWarehouse,
  };
}

type WarehouseLocationContextType = {
  selectedWarehouse: dbui_v1_WarehouseItem | undefined;
  setSelectedWarehouse: (warehouse: dbui_v1_WarehouseItem | undefined) => void;
};

const WarehouseLocationContext = createContext<
  WarehouseLocationContextType | undefined
>(undefined);

export function WarehouseLocationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { selectedWarehouse, setSelectedWarehouse } =
    useWarehouseLocationManager();

  // The warehouse id is used to key the warehouse location context provider
  // This is so that the context provider is recreated when the selected warehouse changes
  const warehouseId = useMemo(
    () => selectedWarehouse?.id ?? 'no-warehouse-selected',
    [selectedWarehouse?.id],
  );

  // The value of the warehouse location context is the selected warehouse and the function to set the selected warehouse
  const value = useMemo(
    () => ({
      selectedWarehouse,
      setSelectedWarehouse,
    }),
    [selectedWarehouse, setSelectedWarehouse],
  );

  return (
    <WarehouseLocationContext.Provider key={warehouseId} value={value}>
      {children}
    </WarehouseLocationContext.Provider>
  );
}

export function useWarehouseLocation() {
  const context = useContext(WarehouseLocationContext);
  if (!context) {
    throw new Error(
      'useWarehouseLocation must be used within a WarehouseLocationProvider',
    );
  }
  return context;
}
